/**
 * Patient Types
 *
 * Types derived from OpenAPI schema for patient-related data.
 */

import { paths } from "@src/packages/openapi/schema";
import { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

/**
 * Response type for GET patient
 */
export type Patient = ResponseBody<
  paths["/{tenantId}/patients/{patientId}"]["get"]
>["data"];

/**
 * Request body type for PATCH patient (update)
 */
export type UpdatePatientPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;

/**
 * Treatment type extracted from patient response
 */
export type Treatment = NonNullable<Patient["treatments"]>[number];

/**
 * Disease enum value from the diseases table
 */
export type DiseaseType = NonNullable<
  NonNullable<
    NonNullable<Patient["medicalHistory"]>["diseases"]
  >[number]["disease"]
>;

/**
 * Treatment intent enum - extracted from OpenAPI schema (legacy, being replaced by TreatmentGoal)
 * Values: "Exploring" | "Preservation" | "Donation" | "Conception" | "StartingTreatment" | "InTreatment" | null
 */
export type TreatmentIntent = Treatment["intent"];

/**
 * Non-nullable treatment intent (excludes null)
 */
export type TreatmentIntentValue = NonNullable<TreatmentIntent>;

/**
 * Fertility intent type extracted from patient response.
 * `goal` now lives here (moved from treatments[]).
 */
export type FertilityIntent = NonNullable<Patient["fertilityIntent"]>;

/**
 * Treatment goal enum - derived from API fertilityIntent.goal.
 */
export type TreatmentGoal = NonNullable<FertilityIntent["goal"]>;

/**
 * Maps legacy treatment intent values to goal values.
 * Used as fallback when fertilityIntent.goal is not set.
 */
const INTENT_TO_GOAL_MAP: Record<TreatmentIntentValue, TreatmentGoal> = {
  Exploring: "exploring",
  Preservation: "egg-preserve",
  Donation: "egg-donation",
  Conception: "pregnancy",
  StartingTreatment: "pregnancy",
  InTreatment: "pregnancy",
};

/**
 * Get the treatment goal from a patient record.
 * Prefers fertilityIntent.goal, falls back to mapping the legacy treatment intent.
 */
export function getPatientGoal(patient: Patient): TreatmentGoal | null {
  if (patient.fertilityIntent?.goal) return patient.fertilityIntent.goal;

  const lastTreatment =
    patient.treatments && patient.treatments.length > 0
      ? patient.treatments[patient.treatments.length - 1]
      : null;
  if (!lastTreatment?.intent) return null;

  // Legacy intent values predate the sex-specific preservation and donation
  // goals. Preserve the behavior of the old journey resolver for those
  // records instead of turning every AMAB history into an egg path.
  if (lastTreatment.intent === "Preservation") {
    return patient.gender === "AMAB" ? "sperm-preserve" : "egg-preserve";
  }
  if (lastTreatment.intent === "Donation") {
    return patient.gender === "AMAB" ? "sperm-donation" : "egg-donation";
  }

  return (
    INTENT_TO_GOAL_MAP[lastTreatment.intent as TreatmentIntentValue] ??
    "exploring"
  );
}
